import { FC, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { SaveButton } from "components/buttons/SaveButton"
import { FormSelect, FormSelectOption } from "components/inputs/FormSelect"
import { ModalConfirmAction } from "components/modals/ModalConfirmAction"

import { getLanguageName } from "constants/languages"

import { settingsService } from "services/settings/settings.service"
import { LocalePrefixMode, LocaleUrlsConfig } from "services/settings/type"

import { SectionCard } from "./SectionCard"

interface LocaleUrlsSectionProps {
  /** The toggles above, which may be unsaved — the backend validates against what is stored. */
  languages: Record<string, boolean>
  localeUrls: LocaleUrlsConfig | undefined
  refreshSettings: () => Promise<void>
}

const FALLBACK: LocaleUrlsConfig = { prefix: "always", default: "en" }

export const LocaleUrlsSection: FC<LocaleUrlsSectionProps> = ({ languages, localeUrls, refreshSettings }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [draft, setDraft] = useState<LocaleUrlsConfig>(localeUrls ?? FALLBACK)
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)

  const enabled = useMemo(
    () =>
      Object.entries(languages ?? {})
        .filter(([, on]) => on)
        .map(([code]) => code),
    [languages]
  )

  const current = localeUrls ?? FALLBACK
  const dirty = draft.prefix !== current.prefix || draft.default !== current.default
  const rootUnavailable = draft.prefix === "as-needed" && !enabled.includes(draft.default)

  const modeOptions: FormSelectOption[] = [
    { value: "always", label: t("settings.localeUrls.modeAlways") },
    { value: "as-needed", label: t("settings.localeUrls.modeAsNeeded") }
  ]

  const rootOptions: FormSelectOption[] = enabled.map((code) => ({
    value: code,
    label: `${code.toUpperCase()} · ${getLanguageName(code)}`
  }))

  const save = async () => {
    setConfirming(false)
    setSaving(true)
    try {
      const ok = await settingsService.updateLocaleUrls(draft)
      if (ok) {
        toast.success(t("settings.localeUrls.updated") as string)
        await refreshSettings()
      }
    } finally {
      setSaving(false)
    }
  }

  const exampleFor = (code: string) => {
    if (draft.prefix === "always" || code !== draft.default) return `/${code}/promotions`

    return "/promotions"
  }

  return (
    <SectionCard title={t("settings.localeUrls.title")}>
      <div className="bg-warning/10 border-warning border px-4 py-3">
        <p className="text-sm font-bold text-warning">{t("settings.localeUrls.warningTitle")}</p>
        <p className="mt-2 text-sm leading-relaxed text-warning">{t("settings.localeUrls.warningRewrites")}</p>
        <p className="mt-2 text-sm leading-relaxed text-warning">{t("settings.localeUrls.warningDeploy")}</p>
        <p className="mt-2 text-sm leading-relaxed text-warning">{t("settings.localeUrls.warningSeoTime")}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:max-w-2xl">
        <FormSelect
          name="localeUrlPrefix"
          label={t("settings.localeUrls.modeLabel")}
          value={draft.prefix}
          disabled={saving}
          options={modeOptions}
          onChange={(event) => setDraft((prev) => ({ ...prev, prefix: event.target.value as LocalePrefixMode }))}
        />

        <FormSelect
          name="localeUrlDefault"
          label={t("settings.localeUrls.rootLabel")}
          value={draft.default}
          disabled={saving || draft.prefix === "always"}
          options={rootOptions}
          onChange={(event) => setDraft((prev) => ({ ...prev, default: event.target.value }))}
          helpText={t("settings.localeUrls.saveLanguagesFirst")}
        />
      </div>

      <div className="rounded-lg border border-black/10 p-3 lg:max-w-2xl dark:border-white/10">
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {t("settings.localeUrls.previewLabel")}
        </p>
        {enabled.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">{t("settings.localeUrls.noLanguages")}</p>
        ) : (
          <ul className="font-mono text-xs leading-relaxed text-gray-700 dark:text-gray-300">
            {enabled.map((code) => (
              <li key={code}>
                {code.toUpperCase()} → {exampleFor(code)}
              </li>
            ))}
          </ul>
        )}
      </div>

      {rootUnavailable && (
        <p className="text-sm font-semibold text-error">
          {t("settings.localeUrls.rootNotEnabled", { language: draft.default.toUpperCase() })}
        </p>
      )}

      <div>
        <SaveButton onClick={() => setConfirming(true)} loading={saving} disabled={!dirty || rootUnavailable} />
      </div>

      {confirming && (
        <ModalConfirmAction
          data={draft}
          handler={() => setConfirming(false)}
          text={t("settings.localeUrls.confirm", {
            mode:
              draft.prefix === "always"
                ? t("settings.localeUrls.modeAlways")
                : t("settings.localeUrls.modeAsNeeded"),
            language: draft.default.toUpperCase()
          })}
          actionText={t("settings.localeUrls.confirmAction") as string}
          actionClassName="btn-error"
          actionHandler={save}
        />
      )}
    </SectionCard>
  )
}
